import React, { useEffect, useMemo, useState } from "react";
import { Alert, Col, Container, Input, Row, Badge, Tooltip } from "reactstrap";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { useCart } from "../context/cartContext";
import { currencyFormatter, getApiUrl, getMediaUrl } from "../utils/site";
import "../styles/product-details.css";
import { FiTrash2, FiPlus, FiMinus, FiHeart, FiShield, FiTruck, FiLock } from "react-icons/fi";

const Cart = () => {
  const { items, updateQuantity, removeItem, clearCart } = useCart();
  const navigate = useNavigate();
  const [recommendations, setRecommendations] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [savedForLater, setSavedForLater] = useState([]);

  const subtotal = useMemo(
    () => items.reduce((sum, item) => sum + Number(item.product.price || 0) * item.quantity, 0),
    [items]
  );
  
  const estimatedShipping = useMemo(() => {
    if (items.length === 0) return 0;
    // Free shipping over $500
    return subtotal >= 500 ? 0 : 25;
  }, [items, subtotal]);

  const taxEstimate = useMemo(() => subtotal * 0.08, [subtotal]);
  const total = useMemo(() => subtotal + estimatedShipping + taxEstimate, [subtotal, estimatedShipping, taxEstimate]);

  useEffect(() => {
    setIsLoading(true);
    axios.get(getApiUrl("/api/products")).then((res) => {
      const all = res.data.data || res.data || [];
      const notInCart = all.filter(
        (product) =>
          !items.some(
            (item) => (item.product.id ?? item.product.uuid) === (product.id ?? product.uuid)
          )
      );
      setRecommendations(notInCart.slice(0, 4));
      setIsLoading(false);
    }).catch(() => setIsLoading(false));
  }, [items]);

  const handleSaveForLater = (item) => {
    setSavedForLater([...savedForLater, item]);
    removeItem(item.product.id ?? item.product.uuid);
  };

  const handleMoveToCart = (item) => {
    // Add item back to cart
    // This would need integration with cart context
    setSavedForLater(savedForLater.filter(i => i.product.id !== item.product.id));
  };

  const handleQuantityChange = (id, change) => {
    const item = items.find(item => (item.product.id ?? item.product.uuid) === id);
    if (item) {
      const newQuantity = Math.max(1, item.quantity + change);
      updateQuantity(id, newQuantity);
    }
  };

  return (
    <main className="roha-page">
      <section className="roha-section">
        <Container>
          {/* Cart Header with Progress Indicator */}
          <div className="roha-cart-header">
            <div className="roha-cart-progress">
              <div className="roha-cart-progress__step roha-cart-progress__step--active">
                <span className="roha-cart-progress__number">1</span>
                <span className="roha-cart-progress__label">Cart</span>
              </div>
              <div className="roha-cart-progress__line"></div>
              <div className="roha-cart-progress__step">
                <span className="roha-cart-progress__number">2</span>
                <span className="roha-cart-progress__label">Shipping</span>
              </div>
              <div className="roha-cart-progress__line"></div>
              <div className="roha-cart-progress__step">
                <span className="roha-cart-progress__number">3</span>
                <span className="roha-cart-progress__label">Payment</span>
              </div>
            </div>

            <div className="roha-section__heading roha-section__heading--row">
              <div>
                <p className="roha-eyebrow">Your Shopping Cart</p>
                <h1 className="section__title">Review your exquisite selection</h1>
                <p className="roha-cart-subtitle">
                  {items.length} {items.length === 1 ? 'item' : 'items'} • {currencyFormatter.format(subtotal)}
                </p>
              </div>
              <Link to="/products" className="roha-button roha-button--ghost">
                <FiPlus className="me-2" /> Continue shopping
              </Link>
            </div>
          </div>

          {items.length === 0 ? (
            <div className="roha-empty-cart">
              <div className="roha-empty-cart__icon">🛒</div>
              <h2>Your cart is empty</h2>
              <p>Discover our collection of fine Ethiopian gemstone jewellery</p>
              <Link to="/products" className="roha-button roha-button--solid">
                Browse Collection
              </Link>
              {savedForLater.length > 0 && (
                <div className="roha-saved-items mt-4">
                  <h4>Saved for later ({savedForLater.length})</h4>
                  <div className="roha-saved-items__grid">
                    {savedForLater.map((item) => (
                      <div key={item.product.id} className="roha-saved-item">
                        <img src={getMediaUrl(item.product.image_url)} alt={item.product.name} />
                        <div>
                          <strong>{item.product.name}</strong>
                          <p>{currencyFormatter.format(Number(item.product.price || 0))}</p>
                          <button 
                            className="roha-button roha-button--small"
                            onClick={() => handleMoveToCart(item)}
                          >
                            Move to cart
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <Row className="g-4">
              <Col lg="8">
                {/* Cart Items with Enhanced UX */}
                <div className="roha-cart-list">
                  {items.map((item) => (
                    <article key={item.product.id ?? item.product.uuid} className="roha-cart-item">
                      <div className="roha-cart-item__image-wrapper">
                        <img
                          src={getMediaUrl(item.product.image_url)}
                          alt={item.product.name}
                          className="roha-cart-item__image"
                        />
                        {item.product.isNew && (
                          <Badge color="gold" className="roha-cart-item__badge">New</Badge>
                        )}
                      </div>
                      <div className="roha-cart-item__content">
                        <div className="roha-cart-item__header">
                          <div>
                            <p className="roha-eyebrow">{item.product.category || "Fine jewellery"}</p>
                            <h3>{item.product.name}</h3>
                            <p className="roha-cart-item__meta">
                              {item.product.material || "Gemstone"} {item.product.weight ? `• ${item.product.weight}g` : ""}
                            </p>
                          </div>
                          <div className="roha-cart-item__price">
                            <strong>{currencyFormatter.format(Number(item.product.price || 0))}</strong>
                            {item.product.originalPrice && (
                              <span className="roha-cart-item__original-price">
                                {currencyFormatter.format(Number(item.product.originalPrice))}
                              </span>
                            )}
                          </div>
                        </div>

                        <div className="roha-cart-item__controls">
                          <div className="roha-cart-qty">
                            <span>Quantity</span>
                            <div className="roha-cart-qty__controls">
                              <button 
                                className="roha-cart-qty__btn"
                                onClick={() => handleQuantityChange(item.product.id ?? item.product.uuid, -1)}
                                disabled={item.quantity <= 1}
                              >
                                <FiMinus />
                              </button>
                              <Input
                                type="number"
                                min={1}
                                value={item.quantity}
                                onChange={(e) =>
                                  updateQuantity(
                                    item.product.id ?? item.product.uuid,
                                    Math.max(1, Number(e.target.value))
                                  )
                                }
                                className="roha-cart-qty__input"
                              />
                              <button 
                                className="roha-cart-qty__btn"
                                onClick={() => handleQuantityChange(item.product.id ?? item.product.uuid, 1)}
                              >
                                <FiPlus />
                              </button>
                            </div>
                          </div>
                          
                          <div className="roha-cart-item__subtotal">
                            <span>Item total</span>
                            <strong>{currencyFormatter.format(Number(item.product.price || 0) * item.quantity)}</strong>
                          </div>
                        </div>

                        <div className="roha-cart-item__actions">
                          <button
                            type="button"
                            className="roha-cart-item__action"
                            onClick={() => handleSaveForLater(item)}
                          >
                            <FiHeart className="me-1" /> Save for later
                          </button>
                          <button
                            type="button"
                            className="roha-cart-item__action roha-cart-item__action--remove"
                            onClick={() => removeItem(item.product.id ?? item.product.uuid)}
                          >
                            <FiTrash2 className="me-1" /> Remove
                          </button>
                        </div>
                      </div>
                    </article>
                  ))}
                </div>

                {/* Cart Actions */}
                <div className="roha-cart-actions">
                  <button 
                    type="button" 
                    className="roha-button roha-button--ghost"
                    onClick={clearCart}
                  >
                    <FiTrash2 className="me-2" /> Clear cart
                  </button>
                  <Link to="/products" className="roha-button roha-button--ghost">
                    Continue shopping
                  </Link>
                </div>

                {/* Trust Badges */}
                <div className="roha-cart-trust">
                  <div className="roha-cart-trust__item">
                    <FiShield />
                    <div>
                      <strong>Authenticity Guaranteed</strong>
                      <p>Certified Ethiopian gemstones with lifetime warranty</p>
                    </div>
                  </div>
                  <div className="roha-cart-trust__item">
                    <FiTruck />
                    <div>
                      <strong>Free Shipping</strong>
                      <p>On orders over {currencyFormatter.format(500)}</p>
                    </div>
                  </div>
                  <div className="roha-cart-trust__item">
                    <FiLock />
                    <div>
                      <strong>Secure Checkout</strong>
                      <p>256-bit SSL encryption & secure payment</p>
                    </div>
                  </div>
                </div>
              </Col>

              {/* Order Summary Sidebar */}
              <Col lg="4">
                <aside className="roha-cart-summary">
                  <p className="roha-eyebrow">Order Summary</p>
                  <h3>Ready for checkout</h3>
                  
                  <div className="roha-cart-summary__rows">
                    <div>
                      <span>Subtotal ({items.length} items)</span>
                      <strong>{currencyFormatter.format(subtotal)}</strong>
                    </div>
                    <div>
                      <span>Shipping</span>
                      <strong>
                        {estimatedShipping === 0 ? (
                          <span className="text-success">FREE</span>
                        ) : (
                          currencyFormatter.format(estimatedShipping)
                        )}
                      </strong>
                    </div>
                    <div>
                      <span>Estimated tax</span>
                      <strong>{currencyFormatter.format(taxEstimate)}</strong>
                    </div>
                  </div>

                  <div className="roha-cart-summary__total">
                    <span>Total</span>
                    <strong>{currencyFormatter.format(total)}</strong>
                  </div>

                  <button 
                    type="button" 
                    className="roha-button roha-button--solid w-100 mb-2"
                    onClick={() => navigate("/checkout")}
                  >
                    Proceed to checkout
                  </button>
                  
                  <div className="roha-cart-summary__note">
                    <p>By completing your purchase, you agree to our <Link to="/terms">Terms of Service</Link></p>
                  </div>

                  {/* Payment Methods */}
                  <div className="roha-cart-payment-methods">
                    <p className="roha-cart-payment-methods__title">We accept</p>
                    <div className="roha-cart-payment-methods__icons">
                      <span>💳</span>
                      <span>🏦</span>
                      <span>📱</span>
                      <span>🔗</span>
                    </div>
                  </div>

                  {/* Promo Code */}
                  <div className="roha-cart-promo">
                    <p className="roha-cart-promo__title">Have a promo code?</p>
                    <div className="roha-cart-promo__input">
                      <Input type="text" placeholder="Enter code" />
                      <button className="roha-button roha-button--small">Apply</button>
                    </div>
                  </div>
                </aside>
              </Col>
            </Row>
          )}

          {/* Recommendations Section */}
          {!!recommendations.length && items.length > 0 && (
            <section className="roha-section roha-cart-recommendations">
              <div className="roha-section__heading">
                <p className="roha-eyebrow">Complete Your Collection</p>
                <h2>You may also like</h2>
                <p>These pieces pair beautifully with your selection</p>
              </div>
              <div className="roha-cart-recommendations__grid">
                {recommendations.map((product) => (
                  <Link
                    key={product.id ?? product.uuid}
                    to={`/products/${product.uuid || product.id}`}
                    className="roha-cart-recommendation"
                  >
                    <div className="roha-cart-recommendation__image">
                      <img src={getMediaUrl(product.image_url)} alt={product.name} />
                      {product.isNew && <Badge color="gold">New</Badge>}
                    </div>
                    <div className="roha-cart-recommendation__content">
                      <strong>{product.name}</strong>
                      <p className="roha-cart-recommendation__category">{product.category}</p>
                      <div className="roha-cart-recommendation__price">
                        {currencyFormatter.format(Number(product.price || 0))}
                        {product.originalPrice && (
                          <span className="roha-cart-recommendation__original-price">
                            {currencyFormatter.format(Number(product.originalPrice))}
                          </span>
                        )}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </Container>
      </section>
    </main>
  );
};

export default Cart;
